import { AVAILABLE_LOCALE_CODES, type LocaleCode } from '@@fire-base/i18n'
import authenticatedMiddleware from './authenticated'
import preOrderBlockMiddleware from './preOrderBlock'
import wishlistGuardMiddleware from './wishlist-guard'

/**
 * Global route middleware.
 *
 * Instead of having Nuxt automatically run each middleware individually,
 * we now have a single global middleware that manually runs each middleware in a sequence.
 *
 * Main reasons:
 * - The auth middleware needs to both redirect to the login page and stop
 *   further middleware execution, if the user is not authenticated.
 *   This did not seem to work as expected. The `abortNavigation` stopped further
 *   middleware execution, but also prevented the redirect to the login page.
 * - No more need to add a number prefix to each middleware file to ensure the correct order.
 *   Can also more easily change the order now, without renaming files.
 */
export default defineNuxtRouteMiddleware(async (to, from) => {
  const { testMode, fallbackLocaleCode } = useRuntimeConfig().public
  if (testMode) {
    return
  }

  // Redirect to default locale if the current locale is not active in the CMS.
  // Extract locale from the URL prefix (strategy: 'prefix').
  const localeFromPath = to.path.split('/')[1] || ''
  const shopConfigStore = useShopConfigStore()
  if (
    AVAILABLE_LOCALE_CODES.includes(localeFromPath as LocaleCode) &&
    !shopConfigStore.isLocaleActive(localeFromPath)
  ) {
    const fallbackPath = to.path.replace(`/${localeFromPath}`, `/${fallbackLocaleCode}`)
    return navigateTo(fallbackPath)
  }

  // NOTE: skip all middleware for public routes, like the login page.
  // This is especially important for the auth middleware, to avoid redirect loops.
  // Other middleware also assume an authenticated user, so they should be skipped as well.
  if (to.meta.isPublic) {
    return
  }

  const authResult = await authenticatedMiddleware(to, from)
  if (authResult) {
    return authResult
  }

  const preOrderBlockResult = preOrderBlockMiddleware(to, from)
  if (preOrderBlockResult) {
    return preOrderBlockResult
  }

  const wishlistReorderOnlyResult = wishlistGuardMiddleware(to, from)
  if (wishlistReorderOnlyResult) {
    return wishlistReorderOnlyResult
  }
})
